import React, { useState, useEffect } from "react";
import axios from "axios";
import "./ConfigPage.css";

const ConfigPage = () => {
  const [redirectUrl, setRedirectUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [currentUrl, setCurrentUrl] = useState("");

  // Load current redirect URL on component mount
  useEffect(() => {
    loadCurrentRedirectUrl();
  }, []);

  const loadCurrentRedirectUrl = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/api/config/redirect");
      if (response.data.success) {
        setCurrentUrl(response.data.redirectUrl);
        setRedirectUrl(response.data.redirectUrl);
      }
    } catch (error) {
      console.error("Error loading redirect URL:", error);
      setStatus({
        type: "error",
        message: "Failed to load current redirect URL",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!redirectUrl.trim()) {
      setStatus({
        type: "error",
        message: "Please enter a valid redirect URL",
      });
      return;
    }

    try {
      setLoading(true);
      setStatus(null);

      const response = await axios.post("/api/config/redirect", {
        redirectUrl: redirectUrl.trim(),
      });

      if (response.data.success) {
        setCurrentUrl(response.data.redirectUrl);
        setStatus({
          type: "success",
          message:
            "Redirect URL saved successfully! Requests and responses will be sent to and received from this URL via POST JSON.",
        });
      } else {
        setStatus({
          type: "error",
          message: response.data.message || "Failed to save redirect URL",
        });
      }
    } catch (error) {
      console.error("Error saving redirect URL:", error);
      setStatus({
        type: "error",
        message: error.response?.data?.message || "Failed to save redirect URL",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setRedirectUrl(currentUrl);
    setStatus(null);
  };

  return (
    <div className="container">
      <div className="card">
        <h2>🔧 Configuration</h2>

        <div className="config-description">
          <p>
            Configure the redirect URL where requests and responses will be sent
            and received. This URL will receive POST JSON requests from the AI
            Voice Bot system.
          </p>
        </div>

        {status && (
          <div className={`status ${status.type}`}>{status.message}</div>
        )}

        <form onSubmit={handleSubmit} className="config-form">
          <div className="form-group">
            <label htmlFor="redirectUrl">Redirect URL:</label>
            <input
              type="url"
              id="redirectUrl"
              value={redirectUrl}
              onChange={(e) => setRedirectUrl(e.target.value)}
              placeholder="https://your-remote-server.com/webhook"
              required
              disabled={loading}
            />
            <small className="form-help">
              Enter the full URL where requests should be redirected (e.g.,
              https://example.com/api/webhook)
            </small>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn" disabled={loading}>
              {loading ? "Saving..." : "Save Configuration"}
            </button>

            {redirectUrl !== currentUrl && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleReset}
                disabled={loading}
              >
                Reset
              </button>
            )}
          </div>
        </form>

        {currentUrl && (
          <div className="current-config">
            <h3>Current Configuration</h3>
            <div className="config-display">
              <strong>Redirect URL:</strong>
              <span className="url-display">{currentUrl}</span>
            </div>
            <div className="config-info">
              <p>
                ✅ All requests and responses will be sent to and received from
                this URL via POST JSON
              </p>
            </div>
          </div>
        )}

        {/* NEW: Sample JSON Response */}
        <div className="sample-response">
          <h4>Sample JSON Request :</h4>
          <div className="json-display">
            <pre>
              <code>{`{
  "message": {
    "timestamp": 1755335847745,
    "type": "tool-calls",
    "toolCalls": [
      {
        "id": "call_KyrfF3Iu4LufO8k8vaBpeVTR",
        "type": "function",
        "function": {
          "name": "mercimed",
          "arguments": {
            "service": "Полировка зубов",
            "date_time": "завтра 12:00",
            "full_name": "Иванови Иван Иванович",
            "birth_date": "15 сентября 1995",
            "specializations": "дантист",
            "qualification_category": "Ортодонт",
            "date_time": "2025-08-17T12:00:00Z"
          }
        }
      }
    ],
    "toolCallList": [
      {
        "id": "call_KyrfF3Iu4LufO8k8vaBpeVTR",
        "type": "function",
        "function": {
          "name": "mercimed",
          "arguments": {
            "service": "Полировка зубов",
            "date_time": "завтра 12:00",
            "full_name": "Иванови Иван Иванович",
            "birth_date": "15 сентября 1995",
            "specializations": "дантист",
            "qualification_category": "Ортодонт",
            "date_time": "2025-08-17T12:00:00Z"
          }
        }
      }
    ]
  }
}`}</code>
            </pre>
          </div>
        </div>

        <div className="sample-response">
          <h4>Sample JSON Response :</h4>
          <div className="json-display">
            <pre>
              <code>{`{
  "results": [
    {
      "toolCallId": "call_KyrfF3Iu4LufO8k8vaBpeVTR",
      "result": "Запись произведена на 2025-08-17T12:00:00Z"
    }
  ]
}`}</code>
            </pre>
          </div>
        </div>
      </div>

      <div className="card">
        <h2>📋 How It Works</h2>
        <div className="workflow">
          <div className="workflow-step">
            <div className="step-number">1</div>
            <div className="step-content">
              <h4>Configure Redirect URL</h4>
              <p>
                Set the URL where your remote server will receive requests and
                send responses.
              </p>
            </div>
          </div>

          <div className="workflow-step">
            <div className="step-number">2</div>
            <div className="step-content">
              <h4>Voice Call Initiation</h4>
              <p>
                When a voice call is made, the system will establish WebRTC SIP
                connection and send webhook events to your remote server.
              </p>
            </div>
          </div>

          <div className="workflow-step">
            <div className="step-number">3</div>
            <div className="step-content">
              <h4>Bidirectional Communication</h4>
              <p>
                Webhook events and responses flow between the AI voice agent,
                your remote server, and the frontend via POST JSON.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigPage;
